import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, FileText, Clock, Menu, X, ChevronDown, ChevronRight, Calculator, Timer, Coffee, Users, User, CalendarDays, FolderKanban, Plus } from 'lucide-react';
import PlanVsActual from './PlanVsActual';
import AuditLogsReport from './AuditLogsReport';
import TimecardsAnalysis from './TimecardsAnalysis';
import TimecardsWeeklyTotals from './TimecardsWeeklyTotals';
import TimecardsEarlyLateReport, { type EarlyLateReportRequest } from './TimecardsEarlyLateReport';
import TimecardsBreakReport, { type BreakReportRequest } from './TimecardsBreakReport';
import StaffSalariedTableReport from './StaffSalariedTableReport';
import AttendanceTimecardVerifier from './AttendanceTimecardVerifier';
import LeaveLedgerReport from './LeaveLedgerReport';
import PayrollRunsPage from './PayrollRunsPage';
import PayrollBuilderPage from './PayrollBuilderPage';
import MyReportsPage from './MyReportsPage';
import PayrollDetailsReport, { type PayrollDetailsReportRunPayload } from './PayrollDetailsReport';
import EmployeeDetailsReport, { type EmployeeDetailsReportRunPayload } from './EmployeeDetailsReport';
import InvoiceReport, { type InvoiceReportSavePayload } from './InvoiceReport';
import ReportHistory, { ReportHistoryItem } from '../components/ReportHistory';
import {
  timecardApi,
  scheduleApi,
  positionApi,
  locationApi,
  payRecordApi,
  companySettingsApi,
  earlyLateReportApi,
  employeeDetailsReportRunsApi,
  invoiceReportRunsApi,
  payrollDetailsReportRunsApi,
  myReportsApi,
  type EarlyLateReportRunListDto,
  type EmployeeDetailsReportRunDetailDto,
  type EmployeeDetailsReportRunListDto,
  type InvoiceReportRunDetailDto,
  type InvoiceReportRunListDto,
  type PayrollDetailsReportRunDetailDto,
  type PayrollDetailsReportRunListDto,
} from '../services/api';
import type { MyReportTemplate } from '../types';
import { createCSVFile, createExcelFile } from '../utils/reportExportFiles';
import {
  buildTimecardAnalysisLookupMaps,
  filterTimecardsForAnalysis,
  generateTimecardAnalysisData,
  type TimecardAnalysisFilterParams,
} from '../utils/timecardAnalysisReport';
import {
  filterTimecardsForWeeklyTotals,
  generateWeeklyTotalsData,
  type WeeklyTotalsFilterParams,
} from '../utils/weeklyTotalsReport';
import {
  buildEarlyLateReport,
  createEarlyLatePdfBlob,
} from '../utils/earlyLateReport';
import {
  buildBreakReport,
  createBreakPdfBlob,
  type BreakTakenFilter,
} from '../utils/breakReport';
import { buildActivePayRecordMap } from '../utils/laborCost';
import {
  filterTimecardsByCommonFilters,
  type TimecardReportCommonFilters,
} from '../utils/timecardReportFilters';
import { appConfirm } from '../context/SystemDialogContext';

function mapEarlyLateServerList(d: EarlyLateReportRunListDto): ReportHistoryItem {
  return {
    id: `elr-${d.id}`,
    serverEarlyLateId: d.id,
    reportType: 'timecards-early-late',
    reportName: 'Early/Late Report',
    parameters: { fromDate: d.fromDate, toDate: d.toDate, netMinutesMin: d.netMinutesMin },
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
    totalAmount: Number(d.totalAmount),
    earlyLateCurrencyLabel: d.currencyLabel,
  };
}

function sortEarlyLateReports(items: ReportHistoryItem[]): ReportHistoryItem[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function mapEmployeeDetailsRunFromList(d: EmployeeDetailsReportRunListDto): ReportHistoryItem {
  return {
    id: `edr-${d.id}`,
    serverEmployeeDetailsRunId: d.id,
    reportType: 'employee-details',
    reportName: 'Employee Details',
    parameters: d.parameters ?? {},
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
  };
}

function mapEmployeeDetailsRunFromDetail(d: EmployeeDetailsReportRunDetailDto): ReportHistoryItem {
  return {
    id: `edr-${d.id}`,
    serverEmployeeDetailsRunId: d.id,
    reportType: 'employee-details',
    reportName: 'Employee Details',
    parameters: d.parameters ?? {},
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
    employeeDetailsSnapshot: {
      headers: d.snapshot?.headers ?? [],
      dataRows: d.snapshot?.dataRows ?? [],
    },
  };
}

function sortEmployeeDetailsReports(items: ReportHistoryItem[]): ReportHistoryItem[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function mapPayrollDetailsRunFromList(d: PayrollDetailsReportRunListDto): ReportHistoryItem {
  return {
    id: `pdr-${d.id}`,
    serverPayrollDetailsRunId: d.id,
    reportType: 'payroll-details',
    reportName: 'Payroll Details',
    parameters: d.parameters ?? {},
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
  };
}

function mapPayrollDetailsRunFromDetail(d: PayrollDetailsReportRunDetailDto): ReportHistoryItem {
  return {
    id: `pdr-${d.id}`,
    serverPayrollDetailsRunId: d.id,
    reportType: 'payroll-details',
    reportName: 'Payroll Details',
    parameters: d.parameters ?? {},
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
    payrollDetailsSnapshot: {
      headers: d.snapshot?.headers ?? [],
      dataRows: d.snapshot?.dataRows ?? [],
    },
  };
}

function sortPayrollDetailsReports(items: ReportHistoryItem[]): ReportHistoryItem[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function mapInvoiceRunFromList(d: InvoiceReportRunListDto): ReportHistoryItem {
  return {
    id: `inv-${d.id}`,
    serverInvoiceReportRunId: d.id,
    reportType: 'schedules-invoice',
    reportName: 'Invoice Report',
    parameters: {
      ...(d.parameters ?? {}),
      scheduleId: d.scheduleId,
      scheduleName: d.scheduleName,
      month: d.month,
      locationId: d.locationId,
      grandTotalHours: d.grandTotalHours,
    },
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
  };
}

function mapInvoiceRunFromDetail(d: InvoiceReportRunDetailDto): ReportHistoryItem {
  return {
    id: `inv-${d.id}`,
    serverInvoiceReportRunId: d.id,
    reportType: 'schedules-invoice',
    reportName: 'Invoice Report',
    parameters: {
      ...(d.parameters ?? {}),
      scheduleId: d.scheduleId,
      scheduleName: d.scheduleName,
      month: d.month,
      locationId: d.locationId,
      grandTotalHours: d.grandTotalHours,
    },
    status: 'completed',
    createdAt: new Date(d.createdAt),
    completedAt: new Date(d.createdAt),
    recordsProcessed: d.recordsProcessed,
    invoiceReportSnapshot: d.snapshot,
  };
}

function sortInvoiceReports(items: ReportHistoryItem[]): ReportHistoryItem[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

type ReportSection =
  | 'plan-vs-actual'
  | 'audit-logs'
  | 'attendance-verifier'
  | 'leave-ledger'
  | 'payroll-details'
  | 'payroll-builder'
  | 'my-reports'
  | 'payroll-runs'
  | 'timecards-analysis'
  | 'timecards-weekly-totals'
  | 'timecards-early-late'
  | 'timecards-break'
  | 'timecards-staff-salaried'
  | 'employee-details'
  | 'schedules-invoice';

function getSectionFromPathname(pathname: string): ReportSection | null {
  if (pathname === '/reports' || pathname === '/reports/') return null;
  if (pathname === '/reports/attendance-verifier') return 'attendance-verifier';
  if (pathname === '/reports/audit-logs') return 'audit-logs';
  if (pathname === '/reports/leave-ledger') return 'leave-ledger';
  if (pathname.includes('timecards-analysis')) return 'timecards-analysis';
  if (pathname.includes('timecards-weekly-totals')) return 'timecards-weekly-totals';
  if (pathname.includes('timecards-early-late')) return 'timecards-early-late';
  if (pathname.includes('timecards-break')) return 'timecards-break';
  if (pathname.includes('timecards-staff-salaried')) return 'timecards-staff-salaried';
  if (pathname.includes('my-reports')) return 'my-reports';
  if (pathname.includes('payroll-builder')) return 'payroll-builder';
  if (pathname.includes('payroll')) return 'payroll-builder';
  if (pathname.includes('employee-details')) return 'employee-details';
  if (pathname.includes('schedules/invoice')) return 'schedules-invoice';
  return null;
}

function getMyReportTemplateId(pathname: string): number | null {
  const match = pathname.match(/\/reports\/my-reports\/(\d+)/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const ReportsEmptyState: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-[320px]">
    <div className="text-center text-neutral-500 px-4">
      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-neutral-300" />
      <p className="text-lg font-medium text-neutral-700">Select a report</p>
      <p className="text-sm mt-1">Choose a report from the sidebar to get started</p>
    </div>
  </div>
);

const Reports: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPayrollRunId = (() => {
    const raw = searchParams.get('runId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const [activeSection, setActiveSection] = useState<ReportSection | null>(() =>
    getSectionFromPathname(location.pathname)
  );

  // Sync active section with URL path for direct links
  useEffect(() => {
    setActiveSection(getSectionFromPathname(location.pathname));
    if (getSectionFromPathname(location.pathname) === 'my-reports') {
      setMyReportsExpanded(true);
    }
  }, [location.pathname]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timecardsExpanded, setTimecardsExpanded] = useState(false);
  const [employeesExpanded, setEmployeesExpanded] = useState(false);
  const [schedulesExpanded, setSchedulesExpanded] = useState(false);
  const [myReportsExpanded, setMyReportsExpanded] = useState(
    () => getSectionFromPathname(location.pathname) === 'my-reports'
  );
  const [myReportTemplates, setMyReportTemplates] = useState<MyReportTemplate[]>([]);
  const myReportTemplateId = getMyReportTemplateId(location.pathname);

  const refreshMyReportTemplates = useCallback(async () => {
    try {
      const rows = await myReportsApi.listTemplates();
      setMyReportTemplates(rows || []);
    } catch (error) {
      console.error('Failed to load My Reports menu items', error);
    }
  }, []);

  useEffect(() => {
    refreshMyReportTemplates().catch(() => undefined);
  }, [refreshMyReportTemplates]);
  const [reportHistory, setReportHistory] = useState<ReportHistoryItem[]>([]);
  /** Employee Details runs: `/api/employee-details-report-runs` (not localStorage). */
  const [employeeDetailsReportRuns, setEmployeeDetailsReportRuns] = useState<ReportHistoryItem[]>([]);
  const [payrollDetailsReportRuns, setPayrollDetailsReportRuns] = useState<ReportHistoryItem[]>([]);
  const [invoiceReportRuns, setInvoiceReportRuns] = useState<ReportHistoryItem[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  const reportHistoryRef = useRef(reportHistory);
  reportHistoryRef.current = reportHistory;
  const employeeDetailsReportRunsRef = useRef(employeeDetailsReportRuns);
  employeeDetailsReportRunsRef.current = employeeDetailsReportRuns;
  const payrollDetailsReportRunsRef = useRef(payrollDetailsReportRuns);
  payrollDetailsReportRunsRef.current = payrollDetailsReportRuns;
  const invoiceReportRunsRef = useRef(invoiceReportRuns);
  invoiceReportRunsRef.current = invoiceReportRuns;

  // Close sidebar when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Report run history (timecard reports): persisted under `reportHistory`.
  // Completed Early/Late rows are server-backed — strip legacy Early/Late from localStorage on load.
  // Blob URLs for CSV/Excel/PDF are not valid after a full page reload.
  useEffect(() => {
    const normalize = (rows: ReportHistoryItem[]) =>
      rows.map((r) => ({
        ...r,
        createdAt: new Date(r.createdAt),
        completedAt: r.completedAt ? new Date(r.completedAt) : undefined,
      }));

    const savedHistory = localStorage.getItem('reportHistory');
    let loaded: ReportHistoryItem[] = [];
    if (savedHistory) {
      const parsed = JSON.parse(savedHistory);
      loaded = normalize(
        parsed.filter(
          (r: ReportHistoryItem) =>
            r.reportType !== 'timecards-early-late' &&
            r.reportType !== 'employee-details' &&
            r.reportType !== 'payroll-details'
        )
      );
    }

    const legacyBreak = localStorage.getItem('breakReportHistory');
    if (legacyBreak) {
      try {
        const breakRows = normalize(JSON.parse(legacyBreak) as ReportHistoryItem[]);
        const existingIds = new Set(loaded.map((r) => r.id));
        loaded = [...breakRows.filter((r) => !existingIds.has(r.id)), ...loaded];
        localStorage.removeItem('breakReportHistory');
      } catch (e) {
        console.error('Failed to migrate break report history', e);
      }
    }

    if (loaded.length > 0) {
      setReportHistory(loaded);
    }
  }, []);

  // Save report history to localStorage (never persist server-backed Early/Late or Employee Details here)
  useEffect(() => {
    const persistable = reportHistory.filter(
      (r) =>
        r.reportType !== 'timecards-early-late' &&
        r.reportType !== 'employee-details' &&
        r.reportType !== 'payroll-details'
    );
    if (persistable.length > 0) {
      localStorage.setItem('reportHistory', JSON.stringify(persistable));
    } else {
      localStorage.removeItem('reportHistory');
    }
  }, [reportHistory]);

  useEffect(() => {
    if (activeSection !== 'timecards-early-late') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await earlyLateReportApi.list(80);
        if (cancelled) return;
        const serverRows = sortEarlyLateReports(list.map(mapEarlyLateServerList));
        setReportHistory((prev) => {
          const other = prev.filter((r) => r.reportType !== 'timecards-early-late');
          const inFlight = prev.filter(
            (r) =>
              r.reportType === 'timecards-early-late' &&
              (r.status === 'pending' || r.status === 'processing' || r.status === 'failed')
          );
          const enriched = serverRows.map((serverRow) => {
            const local = prev.find(
              (r) =>
                r.serverEarlyLateId === serverRow.serverEarlyLateId &&
                r.status === 'completed' &&
                (r.csvUrl || r.earlyLateSnapshot)
            );
            return local ? { ...serverRow, ...local, id: serverRow.id } : serverRow;
          });
          return [...other, ...sortEarlyLateReports([...inFlight, ...enriched])];
        });
      } catch (e) {
        console.error('Failed to load Early/Late report history', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'employee-details') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await employeeDetailsReportRunsApi.list(80);
        if (cancelled) return;
        setEmployeeDetailsReportRuns(sortEmployeeDetailsReports(list.map(mapEmployeeDetailsRunFromList)));
      } catch (e) {
        console.error('Failed to load Employee Details report history', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'payroll-details') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await payrollDetailsReportRunsApi.list(80);
        if (cancelled) return;
        setPayrollDetailsReportRuns(sortPayrollDetailsReports(list.map(mapPayrollDetailsRunFromList)));
      } catch (e) {
        console.error('Failed to load Payroll Details report history', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'schedules-invoice') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await invoiceReportRunsApi.list(80);
        if (cancelled) return;
        setInvoiceReportRuns(sortInvoiceReports(list.map(mapInvoiceRunFromList)));
      } catch (e) {
        console.error('Failed to load Invoice report history', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'employee-details') {
      setEmployeesExpanded(true);
    }
    if (activeSection?.startsWith('timecards')) {
      setTimecardsExpanded(true);
    }
    if (activeSection === 'schedules-invoice') {
      setSchedulesExpanded(true);
    }
  }, [activeSection]);

  // Cleanup blob URLs on unmount (refs so we see latest rows)
  useEffect(() => {
    return () => {
      reportHistoryRef.current.forEach((report) => {
        if (report.csvUrl && report.csvUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.csvUrl);
        }
        if (report.excelUrl && report.excelUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.excelUrl);
        }
        if (report.pdfUrl && report.pdfUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.pdfUrl);
        }
      });
      employeeDetailsReportRunsRef.current.forEach((report) => {
        if (report.csvUrl && report.csvUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.csvUrl);
        }
        if (report.excelUrl && report.excelUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.excelUrl);
        }
        if (report.pdfUrl && report.pdfUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.pdfUrl);
        }
      });
      payrollDetailsReportRunsRef.current.forEach((report) => {
        if (report.csvUrl && report.csvUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.csvUrl);
        }
        if (report.excelUrl && report.excelUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.excelUrl);
        }
        if (report.pdfUrl && report.pdfUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.pdfUrl);
        }
      });
      invoiceReportRunsRef.current.forEach((report) => {
        if (report.pdfUrl && report.pdfUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.pdfUrl);
        }
      });
    };
  }, []);

  const sidebarItems = [
    { id: 'plan-vs-actual' as ReportSection, label: 'Plan Vs Actual', icon: BarChart3, isParent: false },
    { id: 'audit-logs' as ReportSection, label: 'Audit Logs', icon: FileText, isParent: false },
    { id: 'payroll-builder' as ReportSection, label: 'Payroll', icon: Calculator, isParent: false },
  ];

  const timecardsItems = [
    { id: 'timecards-analysis' as ReportSection, label: 'Analysis', icon: BarChart3 },
    { id: 'timecards-weekly-totals' as ReportSection, label: 'Weekly Totals', icon: FileText },
    { id: 'timecards-early-late' as ReportSection, label: 'Early/Late Report', icon: Timer },
    { id: 'timecards-break' as ReportSection, label: 'Break Report', icon: Coffee },
    { id: 'timecards-staff-salaried' as ReportSection, label: 'Staff Salaried table', icon: Users },
  ];

  const employeesItems = [
    { id: 'employee-details' as ReportSection, label: 'Employee Details', icon: FileText },
  ];

  const schedulesItems = [
    { id: 'schedules-invoice' as ReportSection, label: 'Invoice Report', icon: FileText },
  ];

  const handleInvoiceReportSave = useCallback(async (payload: InvoiceReportSavePayload) => {
    try {
      const created = await invoiceReportRunsApi.create({
        scheduleId: payload.scheduleId,
        scheduleName: payload.scheduleName,
        month: payload.month,
        locationId: payload.locationId ?? null,
        recordsProcessed: payload.recordsProcessed,
        grandTotalHours: payload.grandTotalHours,
        parameters: payload.parameters,
        snapshot: payload.snapshot as unknown as Record<string, unknown>,
      });
      const item = mapInvoiceRunFromDetail(created);
      setInvoiceReportRuns((prev) =>
        sortInvoiceReports([item, ...prev.filter((x) => x.id !== item.id)])
      );
      return item;
    } catch (e) {
      console.error(e);
      window.alert('Could not save this report to history.');
      throw e;
    }
  }, []);

  const handleEmployeeDetailsReportRun = useCallback(async (payload: EmployeeDetailsReportRunPayload) => {
    try {
      const created = await employeeDetailsReportRunsApi.create({
        recordsProcessed: payload.recordsProcessed,
        parameters: payload.parameters as unknown as Record<string, unknown>,
        snapshot: { headers: payload.headers, dataRows: payload.rows },
      });
      if (payload.csvUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.csvUrl);
      if (payload.excelUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.excelUrl);
      if (payload.pdfUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.pdfUrl);
      const item = mapEmployeeDetailsRunFromDetail(created);
      setEmployeeDetailsReportRuns((prev) =>
        sortEmployeeDetailsReports([item, ...prev.filter((x) => x.id !== item.id)])
      );
    } catch (e) {
      console.error(e);
      window.alert('Could not save this report run to the server. It was not added to history.');
      throw e;
    }
  }, []);

  const handlePayrollDetailsReportQueued = useCallback((item: ReportHistoryItem) => {
    setPayrollDetailsReportRuns((prev) => sortPayrollDetailsReports([item, ...prev]));
  }, []);

  const handlePayrollDetailsReportPatch = useCallback((localId: string, patch: Partial<ReportHistoryItem>) => {
    setPayrollDetailsReportRuns((prev) =>
      prev.map((r) => (r.id === localId ? { ...r, ...patch } : r))
    );
  }, []);

  const handlePayrollDetailsReportComplete = useCallback(
    async (localId: string, payload: PayrollDetailsReportRunPayload) => {
      try {
        const created = await payrollDetailsReportRunsApi.create({
          recordsProcessed: payload.recordsProcessed,
          parameters: payload.parameters as unknown as Record<string, unknown>,
          snapshot: { headers: payload.headers, dataRows: payload.rows },
        });
        if (payload.csvUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.csvUrl);
        if (payload.excelUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.excelUrl);
        if (payload.pdfUrl?.startsWith('blob:')) URL.revokeObjectURL(payload.pdfUrl);
        const item: ReportHistoryItem = {
          ...mapPayrollDetailsRunFromDetail(created),
          csvUrl: payload.csvUrl,
          excelUrl: payload.excelUrl,
          pdfUrl: payload.pdfUrl,
        };
        setPayrollDetailsReportRuns((prev) =>
          sortPayrollDetailsReports([item, ...prev.filter((r) => r.id !== localId)])
        );
      } catch (e) {
        console.error(e);
        handlePayrollDetailsReportPatch(localId, {
          status: 'failed',
          parameters: {
            ...(payload.parameters ?? {}),
            errorMessage: 'Could not save this report run to the server.',
          },
        });
        throw e;
      }
    },
    [handlePayrollDetailsReportPatch]
  );

  const revokeBlobUrls = (report: ReportHistoryItem) => {
    if (report.csvUrl?.startsWith('blob:')) URL.revokeObjectURL(report.csvUrl);
    if (report.excelUrl?.startsWith('blob:')) URL.revokeObjectURL(report.excelUrl);
    if (report.pdfUrl?.startsWith('blob:')) URL.revokeObjectURL(report.pdfUrl);
  };

  const handleDeleteReport = (reportId: string) => {
    setReportHistory((prev) => {
      const reportToDelete = prev.find((r) => r.id === reportId);
      if (!reportToDelete) return prev;

      if (reportToDelete.serverEarlyLateId != null) {
        void earlyLateReportApi.delete(reportToDelete.serverEarlyLateId).catch((err) => {
          console.error(err);
        });
      }
      revokeBlobUrls(reportToDelete);
      return prev.filter((r) => r.id !== reportId);
    });

    setEmployeeDetailsReportRuns((prev) => {
      const reportToDelete = prev.find((r) => r.id === reportId);
      if (!reportToDelete) return prev;

      if (reportToDelete.serverEmployeeDetailsRunId != null) {
        void employeeDetailsReportRunsApi.delete(reportToDelete.serverEmployeeDetailsRunId).catch((err) => {
          console.error(err);
        });
      }
      if (reportToDelete.csvUrl && reportToDelete.csvUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.csvUrl);
      }
      if (reportToDelete.excelUrl && reportToDelete.excelUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.excelUrl);
      }
      if (reportToDelete.pdfUrl && reportToDelete.pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.pdfUrl);
      }

      return prev.filter((r) => r.id !== reportId);
    });

    setPayrollDetailsReportRuns((prev) => {
      const reportToDelete = prev.find((r) => r.id === reportId);
      if (!reportToDelete) return prev;

      if (reportToDelete.serverPayrollDetailsRunId != null) {
        void payrollDetailsReportRunsApi.delete(reportToDelete.serverPayrollDetailsRunId).catch((err) => {
          console.error(err);
        });
      }
      if (reportToDelete.csvUrl && reportToDelete.csvUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.csvUrl);
      }
      if (reportToDelete.excelUrl && reportToDelete.excelUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.excelUrl);
      }
      if (reportToDelete.pdfUrl && reportToDelete.pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.pdfUrl);
      }

      return prev.filter((r) => r.id !== reportId);
    });

    setInvoiceReportRuns((prev) => {
      const reportToDelete = prev.find((r) => r.id === reportId);
      if (!reportToDelete) return prev;

      if (reportToDelete.serverInvoiceReportRunId != null) {
        void invoiceReportRunsApi.delete(reportToDelete.serverInvoiceReportRunId).catch((err) => {
          console.error(err);
        });
      }
      if (reportToDelete.pdfUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(reportToDelete.pdfUrl);
      }

      return prev.filter((r) => r.id !== reportId);
    });
  };

  const handleClearAllPayrollDetailsReports = useCallback(async () => {
    if (
      !(await appConfirm(
        'Delete all saved Payroll Details report runs? This removes every entry in report run history and cannot be undone.',
        { danger: true, confirmLabel: 'Delete' }
      ))
    ) {
      return;
    }
    try {
      await payrollDetailsReportRunsApi.deleteAll();
      payrollDetailsReportRunsRef.current.forEach((report) => {
        if (report.csvUrl?.startsWith('blob:')) URL.revokeObjectURL(report.csvUrl);
        if (report.excelUrl?.startsWith('blob:')) URL.revokeObjectURL(report.excelUrl);
        if (report.pdfUrl?.startsWith('blob:')) URL.revokeObjectURL(report.pdfUrl);
      });
      setPayrollDetailsReportRuns([]);
    } catch (e) {
      console.error(e);
      window.alert('Could not clear payroll report run history.');
    }
  }, []);

  const reportNameForType = (reportType: ReportHistoryItem['reportType']): string => {
    switch (reportType) {
      case 'timecards-analysis':
        return 'Timecards Analysis';
      case 'timecards-weekly-totals':
        return 'Weekly Totals';
      case 'timecards-early-late':
        return 'Early/Late Report';
      case 'timecards-break':
        return 'Break Report';
      default:
        return 'Report';
    }
  };

  const handleGenerateReport = async (
    request:
      | Omit<ReportHistoryItem, 'id' | 'status' | 'createdAt' | 'reportName'>
      | EarlyLateReportRequest
      | BreakReportRequest
  ) => {
    const reportId = `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const reportName = reportNameForType(request.reportType);

    const newReport: ReportHistoryItem = {
      id: reportId,
      reportType: request.reportType,
      reportName,
      parameters: request.parameters,
      status: 'pending',
      createdAt: new Date(),
    };

    setReportHistory((prev) => [newReport, ...prev]);
    setHistoryExpanded(true);

    setTimeout(async () => {
      setReportHistory((prev) =>
        prev.map((r) => (r.id === reportId ? { ...r, status: 'processing' } : r))
      );

      try {
        const completedAt = new Date();
        const timestamp = completedAt.toISOString().slice(0, 10);
        const baseFilename = `${reportName.replace(/\s+/g, '_')}_${timestamp}`;

        if (request.reportType === 'timecards-analysis') {
          const params = request.parameters as TimecardAnalysisFilterParams;
          const [allTimecards, scheduleRows, positionRows] = await Promise.all([
            timecardApi.getAll(),
            scheduleApi.getAll(),
            positionApi.getAll(),
          ]);
          const timecards = filterTimecardsForAnalysis(allTimecards, params);
          if (!timecards?.length) {
            throw new Error('No timecard data found for the selected filters');
          }
          const lookupMaps = buildTimecardAnalysisLookupMaps(scheduleRows, positionRows);
          const reportData = generateTimecardAnalysisData(timecards, lookupMaps);
          const csvFile = createCSVFile(reportData.headers, reportData.rows, baseFilename);
          const excelFile = createExcelFile(reportData.headers, reportData.rows, baseFilename);

          setReportHistory((prev) =>
            prev.map((r) =>
              r.id === reportId
                ? {
                    ...r,
                    status: 'completed',
                    completedAt,
                    csvUrl: csvFile.url,
                    excelUrl: excelFile.url,
                    recordsProcessed: reportData.rows.length,
                    analysisSnapshot: {
                      headers: reportData.headers,
                      dataRows: reportData.rows,
                    },
                  }
                : r
            )
          );
        } else if (request.reportType === 'timecards-weekly-totals') {
          const params = request.parameters as WeeklyTotalsFilterParams;
          const allTimecards = await timecardApi.getAll();
          const timecards = filterTimecardsForWeeklyTotals(allTimecards, params);
          if (!timecards?.length) {
            throw new Error('No timecard data found for the selected filters');
          }
          const reportData = generateWeeklyTotalsData(
            timecards,
            params.fromWeekStartDate,
            params.toWeekStartDate
          );
          const csvFile = createCSVFile(reportData.headers, reportData.rows, baseFilename);
          const excelFile = createExcelFile(reportData.headers, reportData.rows, baseFilename);

          setReportHistory((prev) =>
            prev.map((r) =>
              r.id === reportId
                ? {
                    ...r,
                    status: 'completed',
                    completedAt,
                    csvUrl: csvFile.url,
                    excelUrl: excelFile.url,
                    recordsProcessed: reportData.rows.length,
                    weeklyTotalsSnapshot: {
                      headers: reportData.headers,
                      dataRows: reportData.rows,
                    },
                  }
                : r
            )
          );
        } else if (request.reportType === 'timecards-early-late') {
          const params = request.parameters as EarlyLateReportRequest['parameters'];
          const [allTimecards, payRecords, locations, scheduleRows, positionRows, companySettings] =
            await Promise.all([
              timecardApi.getAll(),
              payRecordApi.getAll(),
              locationApi.getAll(),
              scheduleApi.getAll(),
              positionApi.getAll(),
              companySettingsApi.get(),
            ]);
          const companyCurrency = companySettings?.currency ?? 'EUR €';
          const payRecordMap = buildActivePayRecordMap(payRecords);
          const locationMap: Record<number, string> = {};
          locations.forEach((loc) => {
            locationMap[loc.id] = loc.name;
          });
          const filtered = filterTimecardsByCommonFilters(
            allTimecards,
            params as TimecardReportCommonFilters
          );
          const lookupMaps = buildTimecardAnalysisLookupMaps(scheduleRows, positionRows);
          const built = buildEarlyLateReport(
            filtered,
            payRecordMap,
            locationMap,
            params.fromDate,
            params.toDate,
            companyCurrency,
            params.netMinutesMin,
            lookupMaps
          );
          if (!built.recordsProcessed) {
            throw new Error('No matching early/late records for the selected filters');
          }

          const csvFile = createCSVFile(built.headers, built.rows, baseFilename);
          const excelFile = createExcelFile(built.headers, built.rows, baseFilename);
          const pdfBlob = createEarlyLatePdfBlob(built.headers, built.rows, {
            fromDate: params.fromDate,
            toDate: params.toDate,
            recordsProcessed: built.recordsProcessed,
            totalAmount: built.totalAmount,
            generatedAt: completedAt,
            currencyLabel: companyCurrency,
            netMinutesMin: params.netMinutesMin,
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const earlyLateSnapshot = {
            headers: built.headers,
            dataRows: built.dataRows,
            currencyLabel: companyCurrency,
            netMinutesMin: params.netMinutesMin,
            parameters: params as Record<string, unknown>,
          };

          const saved = await earlyLateReportApi.create({
            fromDate: params.fromDate,
            toDate: params.toDate,
            currencyLabel: companyCurrency,
            recordsProcessed: built.recordsProcessed,
            totalAmount: built.totalAmount,
            snapshot: {
              headers: built.headers,
              dataRows: built.dataRows as unknown[],
              currencyLabel: companyCurrency,
              netMinutesMin: params.netMinutesMin,
              parameters: params as Record<string, unknown>,
            },
          });

          setReportHistory((prev) =>
            prev.map((r) =>
              r.id === reportId
                ? {
                    ...r,
                    id: `elr-${saved.id}`,
                    serverEarlyLateId: saved.id,
                    status: 'completed',
                    completedAt: new Date(saved.createdAt),
                    csvUrl: csvFile.url,
                    excelUrl: excelFile.url,
                    pdfUrl,
                    recordsProcessed: built.recordsProcessed,
                    totalAmount: built.totalAmount,
                    earlyLateSnapshot,
                    earlyLateCurrencyLabel: companyCurrency,
                  }
                : r
            )
          );
        } else if (request.reportType === 'timecards-break') {
          const params = request.parameters as BreakReportRequest['parameters'];
          const [allTimecards, locations, scheduleRows, positionRows] = await Promise.all([
            timecardApi.getAll(),
            locationApi.getAll(),
            scheduleApi.getAll(),
            positionApi.getAll(),
          ]);
          const locationMap: Record<number, string> = {};
          locations.forEach((loc) => {
            locationMap[loc.id] = loc.name;
          });
          const filtered = filterTimecardsByCommonFilters(
            allTimecards,
            params as TimecardReportCommonFilters
          );
          const lookupMaps = buildTimecardAnalysisLookupMaps(scheduleRows, positionRows);
          const breakTakenFilter = (params.breakTakenFilter ?? '') as BreakTakenFilter;
          const built = buildBreakReport(
            filtered,
            locationMap,
            params.fromDate,
            params.toDate,
            breakTakenFilter,
            lookupMaps
          );
          if (!built.recordsProcessed) {
            throw new Error('No matching break records for the selected filters');
          }

          const csvFile = createCSVFile(built.headers, built.rows, baseFilename);
          const excelFile = createExcelFile(built.headers, built.rows, baseFilename);
          const pdfBlob = createBreakPdfBlob(built.headers, built.rows, {
            fromDate: params.fromDate,
            toDate: params.toDate,
            recordsProcessed: built.recordsProcessed,
            generatedAt: completedAt,
            breakTakenFilter,
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);

          setReportHistory((prev) =>
            prev.map((r) =>
              r.id === reportId
                ? {
                    ...r,
                    status: 'completed',
                    completedAt,
                    csvUrl: csvFile.url,
                    excelUrl: excelFile.url,
                    pdfUrl,
                    recordsProcessed: built.recordsProcessed,
                    breakSnapshot: {
                      headers: built.headers,
                      dataRows: built.dataRows,
                      breakTakenFilter,
                    },
                  }
                : r
            )
          );
        } else {
          throw new Error('Unknown report type');
        }
      } catch (error: unknown) {
        console.error('Error generating report:', error);
        const message = error instanceof Error ? error.message : 'Failed to generate report';
        setReportHistory((prev) =>
          prev.map((r) =>
            r.id === reportId
              ? {
                  ...r,
                  status: 'failed',
                  completedAt: new Date(),
                  error: message,
                }
              : r
          )
        );
      }
    }, 500);
  };

  const renderContent = () => {
    if (!activeSection) {
      return (
        <div className="h-full flex flex-col">
          <div className="h-full">
            <ReportsEmptyState />
          </div>
        </div>
      );
    }

    const content = (() => {
      switch (activeSection) {
        case 'plan-vs-actual':
          return <PlanVsActual />;
        case 'audit-logs':
          return <AuditLogsReport />;
        case 'attendance-verifier':
          return <AttendanceTimecardVerifier />;
        case 'leave-ledger':
          return <LeaveLedgerReport />;
        case 'payroll-builder':
          return (
            <div className="h-full min-h-0 flex flex-col">
              <PayrollBuilderPage />
            </div>
          );
        case 'my-reports':
          return (
            <div className="h-full min-h-0 flex flex-col">
              <MyReportsPage
                templateId={myReportTemplateId}
                onTemplatesChanged={refreshMyReportTemplates}
              />
            </div>
          );
        case 'payroll-details':
          return (
            <PayrollDetailsReport
              onReportRunQueued={handlePayrollDetailsReportQueued}
              onReportRunPatch={handlePayrollDetailsReportPatch}
              onReportRunComplete={handlePayrollDetailsReportComplete}
              runHistory={payrollDetailsReportRuns}
              onDeleteReport={handleDeleteReport}
              onClearAllHistory={handleClearAllPayrollDetailsReports}
              initialPayrollRunId={initialPayrollRunId}
            />
          );
        case 'payroll-runs':
          return (
            <div className="h-full min-h-0 flex flex-col">
              <PayrollRunsPage />
            </div>
          );
        case 'timecards-analysis':
          return <TimecardsAnalysis onGenerateReport={handleGenerateReport} />;
        case 'timecards-weekly-totals':
          return <TimecardsWeeklyTotals onGenerateReport={handleGenerateReport} />;
        case 'timecards-early-late':
          return (
            <TimecardsEarlyLateReport
              onGenerateReport={handleGenerateReport}
              runHistory={reportHistory.filter((r) => r.reportType === 'timecards-early-late')}
            />
          );
        case 'timecards-break':
          return (
            <TimecardsBreakReport
              onGenerateReport={handleGenerateReport}
              runHistory={reportHistory.filter((r) => r.reportType === 'timecards-break')}
            />
          );
        case 'timecards-staff-salaried':
          return <StaffSalariedTableReport />;
        case 'employee-details':
          return (
            <EmployeeDetailsReport
              onReportRun={handleEmployeeDetailsReportRun}
              runHistory={employeeDetailsReportRuns}
              onDeleteReport={handleDeleteReport}
            />
          );
        case 'schedules-invoice':
          return (
            <InvoiceReport
              onSaveRun={handleInvoiceReportSave}
              runHistory={invoiceReportRuns}
              onDeleteRun={handleDeleteReport}
            />
          );
        default:
          return <ReportsEmptyState />;
      }
    })();

    const showHistory =
      activeSection != null &&
      activeSection.startsWith('timecards') &&
      activeSection !== 'timecards-staff-salaried';

    const filteredHistory = activeSection
      ? reportHistory.filter((report) => report.reportType === activeSection)
      : [];

    return (
      <div className="h-full flex flex-col">
        <div className={showHistory ? 'flex-1 overflow-auto' : 'h-full'}>
          {content}
        </div>
        {showHistory && (
          <div className="border-t border-neutral-200 bg-white">
            <ReportHistory
              reports={filteredHistory}
              isExpanded={historyExpanded}
              onToggleExpanded={() => setHistoryExpanded(!historyExpanded)}
              onDeleteReport={handleDeleteReport}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full bg-neutral-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-neutral-200"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed md:relative inset-y-0 left-0 z-40 w-64 bg-white border-r border-neutral-200 transform transition-transform duration-200 ease-in-out flex flex-col ${
          sidebarOpen
            ? 'translate-x-0 pointer-events-auto'
            : '-translate-x-full pointer-events-none md:pointer-events-auto md:translate-x-0'
        }`}
      >
        {/* Sidebar Header */}
        <div className="px-4 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Reports</h2>
          <p className="text-sm text-neutral-600 mt-1">View system reports and analytics</p>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {/* Regular Menu Items */}
            {sidebarItems.slice(0, 1).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setSidebarOpen(false);
                    if (item.id === 'payroll-builder') {
                      navigate('/reports/payroll-builder');
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}

            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setMyReportsExpanded((v) => !v)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  activeSection === 'my-reports'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {myReportsExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                )}
                <FolderKanban className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">My Reports</span>
              </button>
              {myReportsExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-neutral-200 pl-2">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSection('my-reports');
                      setSidebarOpen(false);
                      navigate('/reports/my-reports');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                      activeSection === 'my-reports' && myReportTemplateId == null
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <Plus className="h-4 w-4 flex-shrink-0" />
                    <span>New report</span>
                  </button>
                  {myReportTemplates.map((saved) => (
                    <button
                      key={saved.id}
                      type="button"
                      title={saved.reportTypeName}
                      onClick={() => {
                        setActiveSection('my-reports');
                        setSidebarOpen(false);
                        navigate(`/reports/my-reports/${saved.id}`);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                        myReportTemplateId === saved.id
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-neutral-600 hover:bg-neutral-100'
                      }`}
                    >
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{saved.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {sidebarItems.slice(1).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setSidebarOpen(false);
                    if (item.id === 'payroll-builder') {
                      navigate('/reports/payroll-builder');
                    } else if (item.id === 'my-reports') {
                      navigate('/reports/my-reports');
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}

            {/* Timecards Collapsible Menu */}
            <div className="space-y-1">
              <button
                onClick={() => setTimecardsExpanded(!timecardsExpanded)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  activeSection?.startsWith('timecards')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {timecardsExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                )}
                <Clock className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Timecards</span>
              </button>

              {/* Timecards Sub-menu */}
              {timecardsExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-neutral-200 pl-2">
                  {timecardsItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                          if (item.id === 'timecards-early-late') {
                            navigate('/reports/timecards-early-late');
                          } else if (item.id === 'timecards-break') {
                            navigate('/reports/timecards-break');
                          } else if (item.id === 'timecards-staff-salaried') {
                            navigate('/reports/timecards-staff-salaried');
                          } else if (item.id === 'timecards-analysis') {
                            navigate('/reports/timecards-analysis');
                          } else if (item.id === 'timecards-weekly-totals') {
                            navigate('/reports/timecards-weekly-totals');
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                          activeSection === item.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Schedules collapsible menu */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setSchedulesExpanded(!schedulesExpanded)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  activeSection === 'schedules-invoice'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {schedulesExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                )}
                <CalendarDays className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Schedules</span>
              </button>

              {schedulesExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-neutral-200 pl-2">
                  {schedulesItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                          navigate('/reports/schedules/invoice');
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                          activeSection === item.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Employees collapsible menu */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setEmployeesExpanded(!employeesExpanded)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  activeSection === 'employee-details'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {employeesExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                )}
                <User className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Employees</span>
              </button>

              {employeesExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-neutral-200 pl-2">
                  {employeesItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                          navigate('/reports/employee-details');
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                          activeSection === item.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto w-full min-w-0 relative z-0 p-4 sm:p-5 md:p-6 lg:px-8 lg:py-6">
        <div className="h-full">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Reports;
